// Gemeinsamer Auth-Screen-Stil wie Login.jsx (Gradient, Orb-Hintergrund, Karte, Footer + Version)
import React, { useEffect, useState } from "react";
import { ShieldCheck, Zap } from "lucide-react";
import { useTheme } from "../../context/ThemeContext.jsx";
import { APP_DISPLAY_VERSION } from "../../constants/appVersion.js";
import { getLoginRoosStudioUrl } from "../../constants/authBrandUrls.js";

const JAEGGI_LOGO = "/assets/jaeggi-white.png";
/** WRTK — Marken-PNG (Alpha), Partnerzeile */
const WRTK_LOGO_GLOW = "/assets/wrtk-brand-glow.png";
/** Eigenes Blitz-PNG — Footer-Pill links (Marken-App-Icon) */
const WRTK_BLITZ_MARK = "/assets/wrtk-blitz-mark.png";
/** Roos — Wordmark für helle Flächen / Chip */
const ROOS_LOGO_WORDMARK_DARK = "/assets/roos-studio-logo-dark.png";
/** Roos — Gradient-R (Referenz-Branding) */
const ROOS_ICON_GRADIENT = "/assets/roos-studio-icon-gradient.png";

export const AUTH_PAGE_GRADIENT =
  "text-white bg-[#02040d] bg-[radial-gradient(ellipse_88%_52%_at_50%_-18%,rgba(56,189,248,0.3),transparent_54%)] bg-[radial-gradient(ellipse_50%_38%_at_100%_8%,rgba(167,139,250,0.16),transparent_52%)] bg-[radial-gradient(ellipse_48%_42%_at_0%_92%,rgba(14,165,233,0.11),transparent_50%)] bg-[radial-gradient(ellipse_95%_65%_at_50%_108%,rgba(0,0,0,0.88),transparent_58%)] bg-gradient-to-br from-[#0c1829] via-[#040a12] to-[#010205]";

/** Eingabefelder wie Login (ohne linkes Icon-Padding — für Icon-Zeile `pl-11` ergänzen) */
export const authFieldBaseClassName = [
  "w-full rounded-xl py-2.5 pr-3 outline-none transition-all min-h-[44px] text-[16px] sm:text-[14px]",
  "bg-white/[0.04] border border-white/[0.08] text-white placeholder:text-white/30",
  "focus:bg-white/[0.06] focus:border-blue-500/40 focus:ring-1 focus:ring-blue-500/25",
].join(" ");

export const authFieldErrorClassName =
  "border-red-500/50 focus:border-red-400/60 focus:ring-red-500/20";

export const authLabelClassName =
  "text-[12px] block mb-1.5 font-medium tracking-wide uppercase text-white/45";

export const authIconClassName =
  "absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] pointer-events-none transition-colors text-white/25 group-focus-within:text-blue-500";

/** Wie `Login.jsx` — Eingaben mit `pl-11`, abgerundet, Mobile-Höhe */
export const authLoginInputClassName = [
  "w-full rounded-[0.9rem] py-2.5 max-sm:py-2 sm:py-3 pr-3 pl-11 outline-none transition-all",
  "max-sm:min-h-[46px] min-h-[48px] max-sm:text-[16px] text-[15px] sm:text-[14px]",
  "bg-white/[0.04] border border-white/[0.08] text-white placeholder:text-white/30",
  "focus:bg-white/[0.06] focus:border-blue-500/40 focus:ring-1 focus:ring-blue-500/25",
].join(" ");

export const authOtpCellClassName = [
  "text-center text-lg tracking-widest rounded-xl py-2.5 min-h-[48px] outline-none transition-all",
  "bg-white/[0.04] border border-white/[0.08] text-white",
  "focus:bg-white/[0.07] focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20",
].join(" ");

export function AuthLoginBackground({ isDark = true, rich = false }) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {rich ? (
        <>
          <div className="absolute inset-0 max-sm:opacity-80 bg-[radial-gradient(ellipse_100%_72%_at_50%_118%,rgba(0,0,0,0.82),transparent_57%)]" />
          <div className="absolute inset-0 max-sm:opacity-55 bg-[linear-gradient(198deg,rgba(255,255,255,0.045)_0%,transparent_22%,transparent_58%,rgba(0,0,0,0.55)_100%)]" />
          <div className="absolute inset-x-0 top-0 h-[min(62vh,560px)] max-sm:opacity-45 bg-[radial-gradient(ellipse_88%_58%_at_50%_-10%,rgba(186,230,253,0.32),transparent_56%)]" />
          <div className="absolute inset-x-0 top-[12%] h-[min(48vh,420px)] max-sm:opacity-50 bg-[radial-gradient(ellipse_55%_42%_at_85%_25%,rgba(192,132,252,0.11),transparent_55%)]" />
          <div className="absolute inset-x-0 top-[18%] h-[45vh] max-sm:opacity-50 bg-[radial-gradient(ellipse_70%_50%_at_15%_30%,rgba(250,204,21,0.075),transparent_52%)]" />
          <div className="absolute inset-x-0 bottom-0 h-[min(48vh,520px)] max-sm:opacity-55 bg-[radial-gradient(ellipse_78%_52%_at_50%_102%,rgba(99,102,241,0.24),transparent_60%)]" />
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-[min(42vh,380px)] opacity-[0.14] max-sm:opacity-[0.06]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(90deg, transparent 0px, transparent 52px, rgba(147,197,253,0.22) 53px, rgba(147,197,253,0.22) 54px, transparent 55px, transparent 104px)",
              maskImage: "linear-gradient(to top, black 0%, transparent 85%)",
              WebkitMaskImage: "linear-gradient(to top, black 0%, transparent 85%)",
            }}
          />
          <div
            className="absolute inset-0 opacity-[0.028] max-sm:opacity-[0.012]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.14) 0, transparent 45%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.1) 0, transparent 40%)",
              backgroundSize: "260px 260px, 320px 320px",
              backgroundPosition: "0 0, 80px 40px",
            }}
          />
          <div className="absolute inset-y-0 left-0 w-[48%] max-w-lg max-sm:opacity-35 bg-[linear-gradient(90deg,rgba(255,255,255,0.055),transparent)]" />
          <div className="absolute inset-y-0 right-0 w-[48%] max-w-lg max-sm:opacity-80 bg-[linear-gradient(270deg,rgba(2,6,23,0.58),transparent)]" />
        </>
      ) : null}
      <div
        className={`login-orb absolute max-sm:opacity-45 w-[640px] h-[640px] rounded-full blur-[130px] ${rich ? "bg-blue-400/[0.22]" : "bg-blue-600/[0.1]"}`}
        style={{ top: "-15%", right: "-10%", animationDelay: "0s" }}
      />
      <div
        className={`login-orb absolute max-sm:opacity-45 w-[520px] h-[520px] rounded-full blur-[125px] ${rich ? "bg-indigo-400/[0.16]" : "bg-indigo-500/[0.07]"}`}
        style={{ bottom: "-20%", left: "-10%", animationDelay: "-5s" }}
      />
      <div
        className={`login-orb absolute max-sm:opacity-45 w-[380px] h-[380px] rounded-full blur-[95px] ${rich ? "bg-cyan-400/[0.16]" : "bg-cyan-500/[0.055]"}`}
        style={{ top: "38%", left: "58%", animationDelay: "-10s" }}
      />
      {rich ? (
        <>
          <div
            className="login-orb absolute max-sm:opacity-40 h-[340px] w-[340px] rounded-full bg-violet-400/[0.11] blur-[90px]"
            style={{ bottom: "5%", right: "8%", animationDelay: "-7s" }}
          />
          <div
            className="login-orb absolute max-sm:opacity-40 h-[280px] w-[520px] rounded-full bg-sky-400/[0.07] blur-[100px]"
            style={{ bottom: "-8%", left: "18%", animationDelay: "-12s" }}
          />
        </>
      ) : null}
      {isDark && (
        <div
          className={`absolute inset-0 ${rich ? "opacity-[0.065] max-sm:opacity-[0.028]" : "opacity-[0.03] max-sm:opacity-[0.015]"}`}
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      )}
      {isDark &&
        Array.from({ length: rich ? 28 : 12 }).map((_, i) => (
          <div
            key={i}
            className="spark absolute"
            style={{
              left: `${(i * 71) % 100}%`,
              bottom: `${(i * 23) % 60}px`,
              animationDelay: `${(i * 0.3) % 2.5}s`,
            }}
          />
        ))}
      {/* Mobil: weniger „Wash“, tieferes Panel (liegt über Glows, unter z-10 Inhalt) */}
      <div
        className="absolute inset-0 z-[1] pointer-events-none max-sm:bg-gradient-to-b max-sm:from-black/40 max-sm:via-black/28 max-sm:to-black/52"
        aria-hidden
      />
    </div>
  );
}

/** Typografische Einleitung — `variant="hero"` wie Login-Überschrift (Gradient) */
export function AuthScreenHeadline({ kicker, title, description, className = "", variant = "default" }) {
  const hero = variant === "hero";
  return (
    <header
      className={`text-center ${hero ? "space-y-2 sm:space-y-2 pt-0.5 sm:pt-1" : "space-y-1.5"} ${className}`}
    >
      {kicker ? (
        <p
          className={
            hero
              ? "text-[10px] font-medium uppercase tracking-[0.15em] text-white/38 sm:text-[11px] sm:tracking-[0.16em]"
              : "text-[10px] font-medium uppercase tracking-[0.18em] text-white/40"
          }
        >
          {kicker}
        </p>
      ) : null}
      <h1
        className={
          hero
            ? "text-[1.35rem] font-semibold tracking-tight text-balance leading-tight bg-gradient-to-br from-white via-white to-white/55 bg-clip-text text-transparent sm:text-[1.85rem]"
            : "text-lg sm:text-xl font-semibold tracking-tight text-white text-balance"
        }
      >
        {title}
      </h1>
      {description ? (
        <p className={`leading-snug max-w-[22rem] mx-auto ${hero ? "text-xs sm:text-[13px] text-white/42" : "text-xs sm:text-[13px] text-white/45"}`}>{description}</p>
      ) : null}
    </header>
  );
}

/** Passkey — dezenter Kasten im App-Stil (optional) */
export function AuthHighlightPanel({ children, className = "" }) {
  return (
    <div className={`rounded-xl border border-white/[0.1] bg-white/[0.03] px-3 py-2.5 ${className}`}>{children}</div>
  );
}

/** Formularblock — nur leichte Abgrenzung, kein schwerer „Glas“-Layer */
export function AuthMutedPanel({ children, className = "" }) {
  return (
    <div className={`rounded-xl border border-white/[0.08] bg-white/[0.02] px-4 py-4 sm:px-4 sm:py-4 ${className}`}>
      {children}
    </div>
  );
}

/** `premium`: Login — symmetrische Logo-Slots + Glas; WRTK/Jaeggi gleiche Zielbreite, dezentes Leuchten */
export function AuthBrandingHeader({ variant = "default" }) {
  const premium = variant === "premium";
  const slotH = premium ? "" : "h-10 sm:h-11";
  const slotCenter = premium ? "" : "h-10 w-10 sm:h-11 sm:w-11";
  const partnerLogoMaxW = premium ? "" : "max-w-[min(11.5rem,49vw)] sm:max-w-[12.75rem]";

  const premiumOuter =
    "rounded-xl border border-white/[0.13] bg-gradient-to-b from-white/[0.1] via-white/[0.05] to-white/[0.025] px-3 py-3 shadow-[0_14px_48px_-14px_rgba(0,0,0,0.58),inset_0_1px_0_rgba(255,255,255,0.11),0_0_0_1px_rgba(255,255,255,0.05)] ring-1 ring-white/[0.06] backdrop-blur-[6px] sm:rounded-2xl sm:px-5 sm:py-4 max-sm:border-white/[0.08] max-sm:from-white/[0.04] max-sm:via-white/[0.02] max-sm:to-[rgba(3,8,18,0.52)] max-sm:shadow-[0_12px_32px_-12px_rgba(0,0,0,0.78),inset_0_1px_0_rgba(255,255,255,0.05)] max-sm:ring-white/[0.04]";

  const partnerSlotJaeggi =
    "login-brand-slot flex h-[3.25rem] w-full max-w-[min(11.75rem,48vw)] items-stretch justify-center rounded-xl px-1.5 py-0.5 sm:h-[3.85rem] sm:max-w-[12.75rem] sm:px-2 sm:py-1";
  const partnerSlotWrtk =
    "login-brand-slot login-brand-wrtk-slot flex h-[3.25rem] w-full max-w-[min(11.75rem,48vw)] items-stretch justify-center rounded-xl px-0 py-0 sm:h-[3.85rem] sm:max-w-[12.75rem] sm:px-0.5 sm:py-0";

  return (
    <div className={`login-brand-ambient space-y-2 ${premium ? "sm:space-y-2.5" : "space-y-2.5"}`}>
      <div
        className={
          premium
            ? premiumOuter
            : "rounded-2xl border border-white/[0.1] bg-gradient-to-b from-white/[0.07] to-white/[0.02] shadow-inner px-3 py-3 sm:px-4 sm:py-3.5"
        }
      >
        <div
          className={[
            "grid grid-cols-[1fr_auto_1fr] items-center",
            premium ? "gap-2 sm:gap-3.5" : "justify-items-stretch gap-2 sm:gap-3",
          ].join(" ")}
        >
          {premium ? (
            <>
              <div className="flex min-w-0 justify-center sm:justify-end">
                <div className={partnerSlotWrtk}>
                  <img
                    src={WRTK_LOGO_GLOW}
                    alt="WireTrack"
                    className="login-brand-trio-logo login-brand-trio-logo--wrtk login-brand-trio-logo--wrtk-trio min-h-0 min-w-0 h-full w-full max-h-full max-w-full object-contain object-center"
                    draggable={false}
                  />
                </div>
              </div>
              <div className="login-brand-zap-slot flex h-[3.25rem] w-[3.25rem] shrink-0 items-center justify-center rounded-xl sm:h-[3.85rem] sm:w-[3.85rem]" aria-hidden>
                <Zap
                  className="login-brand-bolt-icon login-brand-bolt-icon--trio-muted h-[1.8rem] w-[1.8rem] shrink-0 text-sky-300 fill-sky-200/88 sm:h-[2.1rem] sm:w-[2.1rem]"
                  strokeWidth={2.35}
                  aria-hidden
                />
              </div>
              <div className="flex min-w-0 justify-center sm:justify-start">
                <div className={partnerSlotJaeggi}>
                  <img
                    src={JAEGGI_LOGO}
                    alt="Jaeggi"
                    className="login-brand-trio-logo login-brand-trio-logo--jaeggi min-h-0 min-w-0 h-full w-full max-h-full max-w-full object-contain"
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={`flex min-w-0 items-center justify-end ${slotH}`}>
                <img
                  src={WRTK_LOGO_GLOW}
                  alt="WireTrack"
                  className={`login-brand-trio-logo login-brand-trio-logo--wrtk h-full w-auto ${partnerLogoMaxW} object-contain object-right`}
                  draggable={false}
                />
              </div>
              <div className={`${slotCenter} login-brand-zap-slot shrink-0 flex items-center justify-center rounded-xl`} aria-hidden>
                <Zap
                  className="login-brand-bolt-icon h-[1.15rem] w-[1.15rem] shrink-0 text-sky-300 fill-sky-200/88 sm:h-4 sm:w-4"
                  strokeWidth={2.35}
                  aria-hidden
                />
              </div>
              <div className={`flex min-w-0 items-center justify-start ${slotH}`}>
                <img
                  src={JAEGGI_LOGO}
                  alt="Jaeggi"
                  className={`login-brand-trio-logo login-brand-trio-logo--jaeggi h-full w-auto ${partnerLogoMaxW} object-contain object-left neon-logo`}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <p
        className={[
          "text-center uppercase tracking-wider text-white/35",
          premium ? "text-[10px] font-medium tracking-[0.15em] text-white/38 sm:text-[11px] sm:tracking-[0.16em]" : "text-[10px]",
        ].join(" ")}
      >
        Elektro Management Platform
      </p>
    </div>
  );
}

/** Verschlüsselungshinweis + Versionszeile — für Login und andere Auth-Pages */
export function AuthFooterMetaLine({ className = "" }) {
  return (
    <div
      className={`flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[11px] tracking-wide text-white/28 ${className}`}
    >
      <ShieldCheck className="w-3.5 h-3.5 shrink-0 text-emerald-400/55" aria-hidden />
      <span>Verschlüsselte Verbindung</span>
      <span aria-hidden className="text-white/15">
        ·
      </span>
      <span className="tabular-nums text-white/35">v{APP_DISPLAY_VERSION}</span>
    </div>
  );
}

/** Kompakter Roos-Chip mit Link — First-Passwort, 2FA, Reset (nicht der grosse Login-Pill) */
export function AuthRoosStudioChipLink({ className = "" }) {
  const href = getLoginRoosStudioUrl();
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.03] px-2.5 py-1 transition-colors hover:bg-white/[0.06] hover:border-white/12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/35 ${className}`}
      aria-label="Roos Studio — Website öffnen"
    >
      <span className="text-[10px] text-white/35 tracking-wide shrink-0 whitespace-nowrap">Entwickelt von</span>
      <img
        src={ROOS_LOGO_WORDMARK_DARK}
        alt="Roos Studio"
        className="h-6 w-auto max-w-[min(10rem,48vw)] shrink-0 object-contain object-left"
      />
    </a>
  );
}

/**
 * Login: „Dual-Pill“ wie Referenz — äussere Kapsel, feiner Strich, rechts verschachtelte Mini-Pill (nur Roos verlinkt).
 * Dunkel-UI-Variante: Glas, Innenlicht, ab sm echte Pillenform (rounded-full).
 */
export function AuthLoginBrandPill() {
  const roosUrl = getLoginRoosStudioUrl();
  const footerBrandIconShell =
    "flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-cyan-400/25 bg-black/80 shadow-[0_0_18px_-4px_rgba(99,102,241,0.32)]";
  const mobileChipShell = [
    "flex w-full max-w-[16.5rem] items-center justify-center gap-2.5 rounded-xl border border-white/[0.12]",
    "bg-gradient-to-b from-white/[0.09] to-white/[0.04] px-3.5 py-2.5",
    "shadow-[0_8px_28px_-10px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.08)]",
  ].join(" ");

  return (
    <footer className="w-full border-t border-white/[0.08] pt-3" aria-label="Produkt und Entwicklung">
      <div className="mx-auto w-full max-w-[min(24rem,calc(100vw-0.5rem))] sm:max-w-lg">
        {/* Mobile: zwei gleichwertige Chips, zentriert — keine Verschachtelung */}
        <div className="flex flex-col items-center gap-2 sm:hidden">
          <div className={mobileChipShell}>
            <span className={footerBrandIconShell} aria-hidden>
              <img
                src={WRTK_BLITZ_MARK}
                alt=""
                className="login-footer-brand-bolt login-footer-brand-bolt--png h-7 w-7 object-contain"
                draggable={false}
              />
            </span>
            <div className="min-w-0 text-center leading-tight">
              <p className="text-[0.8125rem] font-semibold tracking-tight text-white">WireTrack</p>
              <p className="mt-0.5 text-[10px] font-normal leading-snug text-white/45">
                Elektro Management Platform
              </p>
            </div>
          </div>

          <a
            href={roosUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={[
              mobileChipShell,
              "min-h-[44px] transition hover:border-white/18 hover:from-white/[0.11] hover:to-white/[0.05]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/45",
            ].join(" ")}
            aria-label="Roos Studio — Website öffnen"
          >
            <span className={footerBrandIconShell}>
              <img
                src={ROOS_ICON_GRADIENT}
                alt=""
                className="login-roos-icon-gradient h-7 w-7 object-contain"
                draggable={false}
              />
            </span>
            <div className="min-w-0 text-center leading-tight">
              <p className="text-[10px] text-white/40">Entwickelt von</p>
              <p className="text-[0.8125rem] font-semibold text-white/92">Roos Studio</p>
            </div>
          </a>
        </div>

        {/* Desktop: Dual-Pill unverändert */}
        <div
          className={[
            "hidden sm:flex flex-col overflow-hidden rounded-2xl border border-white/[0.12]",
            "bg-gradient-to-b from-white/[0.09] to-white/[0.035]",
            "shadow-[0_10px_40px_-12px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.09)]",
            "sm:flex-row sm:items-center sm:rounded-full sm:p-0.5 sm:pr-1",
          ].join(" ")}
        >
          <div className="flex min-h-[2.65rem] flex-1 items-center gap-2.5 px-3 py-2 sm:min-h-0 sm:gap-3 sm:px-3.5 sm:py-1.5">
            <span className={footerBrandIconShell} aria-hidden>
              <img
                src={WRTK_BLITZ_MARK}
                alt=""
                className="login-footer-brand-bolt login-footer-brand-bolt--png h-7 w-7 object-contain sm:h-8 sm:w-8"
                draggable={false}
              />
            </span>
            <div className="min-w-0 text-left leading-tight">
              <p className="text-[0.75rem] font-semibold tracking-tight text-white sm:text-[0.8125rem]">WireTrack</p>
              <p className="mt-0.5 text-[9px] font-normal leading-snug text-white/45 sm:text-[10px]">
                Elektro Management Platform
              </p>
            </div>
          </div>

          <div className="hidden w-px shrink-0 self-stretch bg-white/[0.12] sm:mx-0.5 sm:block sm:min-h-[2.35rem]" aria-hidden />

          <div className="flex flex-1 items-stretch p-1.5 pt-0 sm:items-center sm:justify-end sm:p-0.5 sm:pl-0.5 sm:pr-0.5 sm:pt-0.5">
            <a
              href={roosUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={[
                "flex w-full min-h-[40px] items-center gap-2 rounded-lg border border-white/[0.11]",
                "bg-white/[0.06] px-2.5 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.07)]",
                "transition hover:border-white/18 hover:bg-white/[0.09]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#060a12]",
                "sm:w-auto sm:min-h-0 sm:rounded-full sm:py-1 sm:pl-1.5 sm:pr-3",
              ].join(" ")}
              aria-label="Roos Studio — Website öffnen"
            >
              <span className={footerBrandIconShell}>
                <img
                  src={ROOS_ICON_GRADIENT}
                  alt=""
                  className="login-roos-icon-gradient h-7 w-7 object-contain sm:h-8 sm:w-8"
                  draggable={false}
                />
              </span>
              <span className="min-w-0 flex-1 text-left text-[10px] leading-snug text-white/48 sm:text-[11px]">
                <span className="block text-white/38">Entwickelt von</span>
                <span className="block font-semibold text-white/92">Roos Studio</span>
              </span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function AuthFooter() {
  return (
    <div className="flex flex-col items-center gap-3 pt-0.5">
      <AuthFooterMetaLine />
      <AuthRoosStudioChipLink />
    </div>
  );
}

export function AuthCardShell({
  children,
  isDark,
  /** z. B. `space-y-6` wenn weniger vertikal Abstand nötig */
  contentClassName = "space-y-7",
  /** Animierter Rand — Login eher aus */
  showGlow = false,
  /** Login: grössere Karte, stärkerer Premium-Schatten */
  premium = false,
}) {
  const shell = premium
    ? "rounded-2xl border border-white/[0.12] bg-gradient-to-b from-white/[0.055] to-white/[0.02] px-4 py-5 shadow-[0_0_0_1px_rgba(255,255,255,0.05),0_36px_120px_-28px_rgba(0,0,0,0.62),0_0_140px_-55px_rgba(56,189,248,0.12)] sm:rounded-[1.35rem] sm:px-8 sm:py-9 max-sm:border-white/[0.09] max-sm:from-white/[0.022] max-sm:to-black/[0.42] max-sm:shadow-[0_0_0_1px_rgba(0,0,0,0.38),0_22px_88px_-34px_rgba(0,0,0,0.82),0_0_100px_-60px_rgba(56,189,248,0.06)]"
    : "rounded-2xl border border-white/[0.1] bg-white/[0.03] px-5 py-6 sm:px-6 sm:py-7 shadow-xl";
  return (
    <div className={`login-card relative ${shell} ${contentClassName}`}>
      {isDark && showGlow ? (
        <div
          className={`login-glow absolute -inset-px pointer-events-none ${premium ? "rounded-2xl sm:rounded-[1.35rem]" : "rounded-2xl"}`}
          aria-hidden="true"
        />
      ) : null}
      {children}
    </div>
  );
}

/**
 * Voller Screen: gleicher Rahmen wie Login — rich-Hintergrund, Premium-Karte,
 * Partnerzeile, Dual-Pill-Footer, Meta-Zeile.
 */
export function AuthPageLayout({
  docTitle,
  /** Ignoriert — gleiche Kartenbreite wie Login (`sm:max-w-[min(32rem,…)]`). */
  maxWidthClass: _unusedMaxWidth,
  contentClassName,
  /** `false`: nur Kinder (ohne Partnerzeile) */
  showBranding = true,
  /** Partnerzeile: wie Login `premium` */
  brandingVariant = "premium",
  children,
}) {
  const { isDark } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (docTitle) document.title = docTitle;
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, [docTitle]);

  return (
    <div className={`login-page min-h-screen w-full relative overflow-hidden ${AUTH_PAGE_GRADIENT}`}>
      <AuthLoginBackground isDark={isDark} rich />
      <div className="relative z-10 flex min-h-screen items-center justify-center px-3 pb-6 pt-4 sm:px-4 sm:py-12">
        <div
          className={`w-full max-w-full sm:max-w-[min(32rem,calc(100vw-1.5rem))] transition-all duration-700 ease-out ${
            mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <AuthCardShell isDark={isDark} premium contentClassName={contentClassName ?? "space-y-3.5 sm:space-y-6"}>
            {showBranding ? (
              <div className="login-hero-reveal login-hero-reveal-d1">
                <AuthBrandingHeader variant={brandingVariant === "premium" ? "premium" : "default"} />
              </div>
            ) : null}
            <div className="login-hero-reveal login-hero-reveal-d2">{children}</div>
            <div className="flex flex-col gap-2 pt-0.5 login-hero-reveal login-hero-reveal-d6 sm:gap-3 sm:pt-1">
              <AuthLoginBrandPill />
              <AuthFooterMetaLine />
            </div>
          </AuthCardShell>
        </div>
      </div>
    </div>
  );
}
